import React, { useEffect, useState } from 'react';
import { Table, DOMHelper, Stack, Divider, SelectPicker, DatePicker, Tag } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { Purchase, PurchasePaginationFilter, SellableFeatureList } from '@/models/marketplace.model';
import { getPagedPurchases } from '@/services/marketplace.service';
import { getUsersLookup } from '@/services/user.service';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import PageContent from '@/components/PageContent';
import FetchingSelect from '@/components/FetchingSelect';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface PurchaseListProps {
  /** Restricts the list to one user's purchases (used from the user detail page). */
  buyerId?: number;
}

const PurchaseList: React.FC<PurchaseListProps> = ({ buyerId }) => {
  const [filterReq, setFilterReq] = useState<PurchasePaginationFilter>(new PurchasePaginationFilter());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: PurchasePaginationFilter) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        buyerId: buyerId || req.buyerId,
      };
      const result: PagedResponseModel<Purchase> = await getPagedPurchases(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize,
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching purchases: ${errorModel?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc',
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="flex-end">
        <Stack spacing={6}>
          {!buyerId && (
            <FetchingSelect
              label="Buyer"
              fetchData={(text, page) => getUsersLookup(text, undefined, page)}
              value={filterReq.buyerId}
              onChange={dataKey => setFilterReq(pre => ({ ...pre, buyerId: dataKey }))}
            />
          )}
          <FetchingSelect
            label="Seller"
            fetchData={(text, page) => getUsersLookup(text, undefined, page)}
            value={filterReq.sellerId}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, sellerId: dataKey }))}
          />
          <SelectPicker
            label="Feature"
            data={SellableFeatureList}
            searchable={false}
            value={filterReq.feature ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, feature: dataKey ?? undefined }))}
          />
          <DatePicker
            label="From"
            format="yyyy-MM-dd"
            value={filterReq.fromDate ?? null}
            onChange={value => setFilterReq(pre => ({ ...pre, fromDate: value ?? undefined }))}
          />
          <DatePicker
            label="To"
            format="yyyy-MM-dd"
            value={filterReq.toDate ?? null}
            onChange={value => setFilterReq(pre => ({ ...pre, toDate: value ?? undefined }))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={1} align="center">
          <HeaderCell>Feature</HeaderCell>
          <Cell>
            {(rowData: Purchase) => (
              <Tag color="blue">
                {SellableFeatureList.find(f => f.value === rowData.feature)?.label ?? rowData.feature}
              </Tag>
            )}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" sortable>
          <HeaderCell>Item</HeaderCell>
          <Cell dataKey="itemId" />
        </Column>
        {!buyerId && (
          <Column flexGrow={1} align="left" sortable>
            <HeaderCell>Buyer</HeaderCell>
            <Cell>{(rowData: Purchase) => rowData.buyer?.username ?? rowData.buyerId}</Cell>
          </Column>
        )}
        <Column flexGrow={1} align="left" sortable>
          <HeaderCell>Seller</HeaderCell>
          <Cell>{(rowData: Purchase) => rowData.seller?.username ?? rowData.sellerId}</Cell>
        </Column>
        <Column flexGrow={0.7} align="right" sortable>
          <HeaderCell>Listed</HeaderCell>
          <Cell dataKey="listedPrice" />
        </Column>
        <Column flexGrow={0.7} align="right" sortable>
          <HeaderCell>Paid</HeaderCell>
          <Cell>
            {(rowData: Purchase) => (
              <span style={{ color: '#e55', fontWeight: 600 }}>{rowData.pricePaid}</span>
            )}
          </Cell>
        </Column>
        <Column flexGrow={0.7} align="right" sortable>
          <HeaderCell>Commission</HeaderCell>
          <Cell dataKey="commissionAmount" />
        </Column>
        <Column flexGrow={0.7} align="right" sortable>
          <HeaderCell>To Seller</HeaderCell>
          <Cell>
            {(rowData: Purchase) => (
              <span style={{ color: '#2db85d', fontWeight: 600 }}>+{rowData.sellerAmount}</span>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Created At</HeaderCell>
          <Cell fullText>{(rowData: Purchase) => jsDateToZone(rowData.createdAt)}</Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />
    </PageContent>
  );
};

export default PurchaseList;
